import React from 'react';
import { Link } from 'react-router-dom';
import { PartyPopper, XCircle, ArrowRight, ChefHat, Utensils } from 'lucide-react';
import { useDossierContext } from './SuiviPrivatisationLayout';
import { PipelineStepperBar } from './PipelineStepperBar';
import { PipelineActionPanel } from './PipelineActionPanel';
import { CreerPrivatisationPanel } from './CreerPrivatisationPanel';
import { getNextTransition, getSkipTransition, getAbandonTransition, getBackTransition, getDevisAccepteHorsPlateforme } from './pipelineTransitions';
import { isTerminalStage, PIPELINE_STAGE_LABELS } from './pipelineStages';
import { moduleApi } from '../../lib/api';
import { useConfirm } from '../ui/ConfirmProvider';
import { pillDangerGhost, pillSecondary, pillWarn } from '../ui/pills';
import { DossierBriefPage } from './DossierBriefPage';
import { PipelineDocuments } from './PipelineDocuments';
import { DossierMenusPage } from './DossierMenusPage';
import { DossierJournalPage } from './DossierJournalPage';
import { Carte, GRILLE_2 } from './dossier/DossierCarte';
import { ClientCard, ServiceCard } from './dossier/DossierFaits';
import { SuiviChiffre } from './dossier/SuiviChiffre';
import { useDossierConvives } from './dossier/useDossierConvives';
import { RendezVousCard } from './dossier/RendezVousCard';
import { DossierStatut } from './dossier/DossierStatut';
import { AbandonDossierModal } from './dossier/AbandonDossierModal';

/**
 * Le corps de la fiche-dossier (maquette 18a, refonte design lot 9), dans l'ordre de la maquette :
 * avancement, ce qui bloque l'étape en cours, les faits (client / service), le suivi chiffré, puis
 * documents et journal — et, entre les deux, ce que l'appli avait déjà et que la maquette ne dessinait
 * pas (rendez-vous, Menus & Jour J, brief) : rien ne disparaît, tout prend la même enveloppe.
 *
 * Aucun changement de logique métier : mêmes routes API, mêmes règles de validation, le serveur reste seul
 * juge (PIPELINE_TRANSITIONS côté functions/api/[[path]].js) — seul l'agencement visuel change.
 */
export function DossierPipelinePage() {
    const { dossier, rafraichir } = useDossierContext();

    const [saving, setSaving] = React.useState(false);
    const confirmAction = useConfirm();
    const [erreurAction, setErreurAction] = React.useState('');
    const [retourEnCours, setRetourEnCours] = React.useState(false);
    const [abandonOuvert, setAbandonOuvert] = React.useState(false);
    const [erreurAbandon, setErreurAbandon] = React.useState('');

    const appliquer = async (payload: Record<string, any>) => {
        setSaving(true);
        setErreurAction('');
        try {
            await moduleApi.transitionDossierPrivatisation(dossier.dossier_id, payload);
            rafraichir();
            return true;
        } catch (e: any) {
            setErreurAction(e?.message || "L'étape n'a pas pu être enregistrée.");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const creerPrivatisation = async (payload: Record<string, any>) => {
        setSaving(true);
        setErreurAction('');
        try {
            await moduleApi.createEventFromDossier(dossier.dossier_id, payload);
            rafraichir();
        } catch (e: any) {
            setErreurAction(e?.message || "La privatisation n'a pas pu être créée.");
        } finally {
            setSaving(false);
        }
    };

    const abandonner = async (motif: string) => {
        const abandon = getAbandonTransition(dossier.stage);
        if (!abandon) return;
        setSaving(true);
        setErreurAbandon('');
        try {
            await moduleApi.transitionDossierPrivatisation(dossier.dossier_id, { to_stage: abandon.nextStage, lost_reason: motif });
            setAbandonOuvert(false);
            rafraichir();
        } catch (e: any) {
            setErreurAbandon(e?.message || "Le dossier n'a pas pu être annulé.");
        } finally {
            setSaving(false);
        }
    };

    // Bouton discret de retour en arrière : efface l'horodatage de l'étape quittée, ne détruit rien d'autre
    // (documents, réponses, devis, montants conservés) — demande d'Emeni, 2026-08-31.
    const revenirEnArriere = async () => {
        const retour = getBackTransition(dossier.stage);
        if (!retour) return;
        if (!(await confirmAction({ title: "Revenir à l'étape précédente ?", description: <>Le dossier repassera à « {retour.label} ». L'horodatage de l'étape actuelle sera effacé — documents, réponses et devis restent intacts.</>, confirmLabel: 'Revenir à cette étape', destructive: false }))) return;
        setRetourEnCours(true);
        setErreurAction('');
        try {
            await moduleApi.transitionDossierPrivatisation(dossier.dossier_id, { back: true });
            rafraichir();
        } catch (e: any) {
            setErreurAction(e?.message || 'Impossible de revenir en arrière.');
        } finally {
            setRetourEnCours(false);
        }
    };

    const docDevisSigne = dossier.documents.find((d) => d.id === dossier.devis_signed_document_id) || null;
    const suivante = getNextTransition(dossier.stage, { hasSignedDevis: !!dossier.devis_signed_received_at });
    const passer = getSkipTransition(dossier.stage);
    const accepte = getDevisAccepteHorsPlateforme(dossier.stage);
    const abandon = getAbandonTransition(dossier.stage);
    const retour = getBackTransition(dossier.stage);
    const clos = isTerminalStage(dossier.stage) || dossier.stage === 'cloturee';
    const eventId = dossier.event?.id || null;

    // Les chiffres de Menus & Convives : lus, jamais recalculés. Ils nourrissent le suivi chiffré et, à
    // l'étape de la collecte, le vrai blocage (« 12 convives sur 108 n'ont pas encore choisi leur plat »).
    const stats = useDossierConvives(eventId);
    const blocageCollecte = dossier.stage === 'collecte_menus' && stats && stats.invites > 0
        ? (stats.silencieux > 0
            ? `${stats.silencieux} convive${stats.silencieux > 1 ? 's' : ''} sur ${stats.invites} n'${stats.silencieux > 1 ? 'ont' : 'a'} pas encore choisi ${stats.silencieux > 1 ? 'leur' : 'son'} plat. Passé la clôture des réponses, la cuisine part sur ${stats.reponses} couverts.`
            : `Les ${stats.invites} convives invités ont tous répondu : la cuisine part sur ${stats.reponses} couverts.`)
        : null;

    return (
        <>
            <PipelineStepperBar dossier={dossier} />

            <div className="px-5 py-4 space-y-3.5">
                {clos ? (
                    <div className="rounded-xl border border-border bg-surface p-6 text-center space-y-1.5">
                        {isTerminalStage(dossier.stage)
                            ? <XCircle size={22} className="mx-auto text-state-danger" />
                            : <PartyPopper size={22} className="mx-auto text-state-ok" />}
                        <p className="text-[13px] font-bold text-content">{PIPELINE_STAGE_LABELS[dossier.stage]}</p>
                        <p className="text-[12.5px] text-content-muted">Ce dossier n'attend plus d'action.</p>
                    </div>
                ) : suivante?.opensForm === 'creer_privatisation' ? (
                    <CreerPrivatisationPanel
                        label={dossier.label}
                        saving={saving}
                        error={erreurAction}
                        backLabel={retour?.label}
                        onConfirm={creerPrivatisation}
                        onBack={retour ? revenirEnArriere : undefined}
                        briefPrefill={dossier.brief_prefill}
                    />
                ) : suivante ? (
                    <PipelineActionPanel
                        label={dossier.label}
                        transition={suivante}
                        skipTransition={passer}
                        accepteTransition={accepte}
                        backLabel={retour?.label}
                        destinataire={dossier.organizer_email || dossier.email}
                        destinataireEstReferent={!!dossier.organizer_email}
                        dossierId={dossier.dossier_id}
                        saving={saving}
                        error={erreurAction}
                        onConfirm={appliquer}
                        onBack={retour ? revenirEnArriere : undefined}
                        apercuDevisSigne={docDevisSigne ? { dossierId: dossier.dossier_id, document: docDevisSigne } : null}
                        blocage={blocageCollecte}
                        actionBlocage={blocageCollecte && eventId && stats && stats.silencieux > 0 ? (
                            <Link to={`/menus-convives/${eventId}`} className={pillWarn(32)}>Relancer les convives</Link>
                        ) : undefined}
                    />
                ) : null}

                {retourEnCours && <p className="text-[12px] text-content-muted">Retour à l'étape précédente...</p>}

                <DossierStatut dossier={dossier} />

                <div className={GRILLE_2}>
                    <ClientCard dossier={dossier} />
                    <ServiceCard dossier={dossier} />
                </div>

                <div className={GRILLE_2}>
                    <RendezVousCard dossier={dossier} rafraichir={rafraichir} />
                    <Carte titre="Menus & Jour J"><DossierMenusPage /></Carte>
                </div>

                <SuiviChiffre couvertsDevis={dossier.event?.num_people} stats={stats} />

                <Carte titre="Brief"><DossierBriefPage /></Carte>

                <div className={GRILLE_2}>
                    <Carte titre="Documents" id="documents">
                        <PipelineDocuments dossierId={dossier.dossier_id} documents={dossier.documents} />
                    </Carte>
                    <Carte titre="Journal du dossier"><DossierJournalPage /></Carte>
                </div>
            </div>

            {/* Pied de fiche (maquette 18a) : l'issue du dossier à gauche, les raccourcis vers la suite du
                parcours à droite. L'action d'avancement, elle, reste attachée à ses champs dans le bandeau de
                l'étape (décision d'Emeni, 16/09). « Exporter la fiche en PDF » n'est pas repris : rien dans
                le module ne sait produire une fiche de dossier. */}
            <div className="flex items-center justify-between gap-2.5 flex-wrap px-5 py-3.5 border-t border-border bg-surface-soft">
                {abandon ? (
                    <button type="button" className={pillDangerGhost()} onClick={() => { setErreurAbandon(''); setAbandonOuvert(true); }} disabled={saving}>
                        {abandon.actionLabel}
                    </button>
                ) : <span />}
                <div className="flex items-center gap-2 flex-wrap">
                    {eventId && (
                        <Link to={`/menus-convives/${eventId}`} className={pillSecondary()}>
                            <Utensils size={14} /> Menus &amp; Convives
                        </Link>
                    )}
                    {eventId && (
                        <Link to={`/mode-terrain/${eventId}`} className={pillSecondary()}>
                            <ChefHat size={14} /> Mode terrain <ArrowRight size={14} />
                        </Link>
                    )}
                </div>
            </div>

            {abandonOuvert && abandon && (
                <AbandonDossierModal
                    label={dossier.label}
                    transition={abandon}
                    saving={saving}
                    error={erreurAbandon}
                    onConfirm={abandonner}
                    onClose={() => setAbandonOuvert(false)}
                />
            )}
        </>
    );
}
